import { normalizeModalSize, modalDimensionCss } from './modalWindowSize'

export function normalizeGeoTargetings(targetings = {}) {
  const result = { ...targetings }
  const kinds = ['country', 'city']
  kinds.forEach(kind => {
    const field = kind === 'country' ? 'countries' : 'cities'
    const current = targetings?.[kind] || {}
    const values = current.params?.[field]
    result[kind] = {
      ...current,
      status: current.status || 'inactive',
      processor: current.processor || (`core.targetings.${kind === 'country' ? 'Country' : 'City'}Targeting`),
      params: { ...current.params, [field]: Array.isArray(values) && values.length ? [...values] : null },
    }
  })
  return result
}

export function geoTargetingErrors(targetings = {}) {
  const errors = {}
  const kinds = ['country', 'city']
  kinds.forEach(kind => {
    const current = targetings?.[kind]
    if (!current) return
    const values = current.params?.[kind === 'country' ? 'countries' : 'cities']
    if (!['inactive', 'active', 'negative'].includes(current.status)
      || (current.status !== 'inactive' && (!Array.isArray(values) || !values.length
        || values.some(value => typeof value !== 'string' || !value.trim())))) errors[kind] = true
  })
  return errors
}

/**
 * Transform API response to form data structure
 * @param {Object} apiData - Data from API
 * @returns {Object} - Form data structure
 */
export function apiToForm(apiData) {
  if (!apiData) return {}

  // Handle GPT structure
  // API can have: gpt.adUnitPath (string path) and gpt.adSize (array of sizes)
  // Or legacy: gpt.adUnitPath as array [300, 200, 320, 250, ...] (sizes)
  let gptAdUnitPath = apiData.gpt?.adUnitPath
  let gptAdSize = []

  // Check if API has separate adSize field
  if (apiData.gpt?.adSize && Array.isArray(apiData.gpt.adSize)) {
    // API has adSize field - convert from array of strings to array of arrays
    // Backend format: ["300x250", "320x100", "fluid"] or ["300,250", "320,100", "fluid"]
    gptAdSize = apiData.gpt.adSize.map(size => {
      // If already an array, return as is
      if (Array.isArray(size)) {
        return size
      }
      // If it's 'fluid' string, return as is
      if (size === 'fluid' || size === '"fluid"') {
        return 'fluid'
      }
      // If it's a string, try to parse it
      if (typeof size === 'string') {
        // Try format "300x250" or "300,250" or "300, 250" or "[300,250]"
        const match = size.match(/\[?(\d+)[,\s]*x?[,\s]*(\d+)\]?/i)
        if (match) {
          return [parseInt(match[1], 10), parseInt(match[2], 10)]
        }
        // If no match, try JSON parse for "[300,250]"
        try {
          const parsed = JSON.parse(size)
          if (Array.isArray(parsed) && parsed.length === 2) {
            return parsed
          }
        } catch (e) {
          // Not JSON, skip
        }
      }
      // Fallback: return as is
      return size
    })
  } else if (Array.isArray(gptAdUnitPath) && gptAdUnitPath.length > 0) {
    // Legacy: adUnitPath is array of sizes, convert to pairs
    const sizes = []
    for (let i = 0; i < gptAdUnitPath.length; i += 2) {
      if (i + 1 < gptAdUnitPath.length) {
        sizes.push([gptAdUnitPath[i], gptAdUnitPath[i + 1]])
      }
    }
    gptAdSize = sizes
    // Set adUnitPath to default string path
    gptAdUnitPath = '/22247219933,227399588/Chordzaa_Adx/phoenixinterstitial'
  } else {
    // adUnitPath is string path (normal case)
    gptAdUnitPath = gptAdUnitPath || '/22247219933,227399588/Chordzaa_Adx/phoenixinterstitial'
  }

  // Handle fluid field - add to adSize if present
  if (apiData.gpt?.fluid === 'fluid' && !gptAdSize.includes('fluid')) {
    gptAdSize.push('fluid')
  }

  // Handle texts structure - use from API if available, otherwise use defaults
  const defaultTexts = {
    title: 'Уважаемый читатель',
    message: 'Пожалуйста, посмотрите короткую рекламу,<br> чтобы поддержать сайт.',
    continueBtn: 'Продолжить',
    backBtn: 'Назад',
    timerText: 'Вознаграждение через {seconds} с',
    adLoading: 'Загрузка рекламы...',
    pleaseWait: 'Пожалуйста, подождите',
  }
  // Use texts from API if available, merge with defaults for any missing fields
  const texts = { ...defaultTexts, ...(apiData.texts || {}) }
  const textsOptions = Array.isArray(apiData.texts_options)
    ? apiData.texts_options.map(option => ({
      ...defaultTexts,
      supportTitle: '',
      supportMessage1: '',
      supportMessage2: '',
      ...option,
    }))
    : []

  // Handle styles structure - deep merge with defaults so missing sub-fields are filled
  // Default values match phx-modal.js widget CSS (base font-size: 16px)
  const defaultStyles = {
    title: { color: '#1a1a1a', fontSize: ['20px', '14px'] },
    message: { color: '#555555', fontSize: ['18px', '14px'] },
    timer: { color: '#6c757d', fontSize: ['14px', '14px'] },
    button: {
      color: '#ffffff',
      backgroundColor: '#1976d2',
      hoverBackgroundColor: '#1565c0',
      fontSize: ['16px', '16px'],
    },
  }
  const styles = { modal: normalizeModalSize(apiData.styles?.modal) }
  Object.keys(defaultStyles).forEach(key => {
    const apiStyle = apiData.styles?.[key] || {}
    const def = defaultStyles[key]
    styles[key] = { ...def, ...apiStyle }
    // Ensure fontSize is a valid array with two entries
    if (!Array.isArray(styles[key].fontSize) || styles[key].fontSize.length < 2) {
      styles[key].fontSize = def.fontSize
    }
  })

  // Prefer the current In-Image structure and fall back to legacy records.
  let inImageAdSize = []
  const rawInImageAdSize = apiData.inImage?.adSize || apiData.inImageGpt?.adSize
  if (Array.isArray(rawInImageAdSize)) {
    inImageAdSize = rawInImageAdSize.map(size => {
      if (Array.isArray(size)) return size
      if (size === 'fluid' || size === '"fluid"') return 'fluid'
      if (typeof size === 'string') {
        const match = size.match(/\[?(\d+)[,\s]*x?[,\s]*(\d+)\]?/i)
        if (match) return [parseInt(match[1], 10), parseInt(match[2], 10)]
        try {
          const parsed = JSON.parse(size)
          if (Array.isArray(parsed) && parsed.length === 2) return parsed
        } catch (e) { /* Not JSON */ }
      }
      return size
    })
  }

  const inImageSlots = []
  const rawInImageSlots = apiData.inImage?.slots || apiData.inImageGpt?.slots
  if (Array.isArray(rawInImageSlots)) {
    rawInImageSlots.forEach(slot => {
      inImageSlots.push(typeof slot === 'string' ? slot : slot?.adUnitPath || '')
    })
  }
  // Ensure at least 1 slot
  if (inImageSlots.length === 0) {
    inImageSlots.push('')
  }

  return {
    _id: apiData._id,
    title: apiData.name || '',
    user_id: apiData.user_id,
    status: apiData.status || 'active',
    phoenix_rate: apiData.phoenix_rate ?? '',
    provider_id: apiData.provider_id ?? '',
    partner_rate: apiData.partner_rate ?? '',
    config: {
      enableModal: apiData.enableModal ?? true,
      enableImpressions: Boolean(apiData.enableImpressions ?? false),
      targetings: normalizeGeoTargetings(apiData.targetings),
      enableInImage: apiData.enableInImage ?? false,
      enableFallback: apiData.enableFallback ?? true,
      enableUserPercentage: Boolean(apiData.enableUserPercentage ?? false),
      userPercentage: Number(apiData.userPercentage ?? 100),
      enablePageCounter: Boolean(apiData.enablePageCounter ?? false),
      pageCounterThreshold: Number(apiData.pageCounterThreshold ?? 3),
      adType: apiData.adType || 'gpt',
      timerDuration: apiData.timerDuration ?? 5,
      showDelay: apiData.showDelay ?? 2000,
      blurOffset: apiData.blurOffset ?? 0,
      frequencyCappingHours: apiData.frequencyCappingHours ?? 2,
      hideIfNoAd: apiData.hideIfNoAd !== undefined ? apiData.hideIfNoAd : true,
      logo: apiData.logoUrl || 'https://nakonu.com/wp-content/uploads/2022/11/logo.png',
      gpt: {
        adUnitPath: gptAdUnitPath,
        adUnitPathRewarded: apiData.gpt?.adUnitPathRewarded || '',
        siteDomain: apiData.gpt?.siteDomain || '',
        contentSelector: apiData.gpt?.contentSelector || '',
        adSize: gptAdSize.length > 0 ? gptAdSize : [[336, 280], [970, 250], [300, 250]],
      },
      adsense: {
        adClient: apiData.adsense?.adClient || null,
        adSlot: apiData.adsense?.adSlot || null,
        adSize: apiData.adsense?.adSize || [300, 250],
      },
      inImage: {
        contentSelector: apiData.inImage?.contentSelector || '',
        maxOverlap: apiData.inImage?.maxOverlap ?? 30,
        frequencyCappingHours: apiData.inImage?.frequencyCappingHours ?? apiData.frequencyCappingHours ?? 2,
        adSize: inImageAdSize.length > 0 ? inImageAdSize : [[300, 250]],
        slots: inImageSlots,
      },
      texts,
      textsOptions,
      styles,
    },
  }
}

/**
 * Transform form data to API request structure
 * @param {Object} formData - Data from form
 * @returns {Object} - API request structure
 */
export function formToApi(formData, { canEditPhoenixRate = true, canEditProviderSettings = false } = {}) {
  if (!formData) return {}

  const apiData = {
    name: formData.title || '',
    user_id: formData.user_id,
    status: formData.status || 'active',
    phoenix_rate: formData.phoenix_rate !== undefined && formData.phoenix_rate !== '' ? Number(formData.phoenix_rate) : 0,
    is_moderate: false,
    enableModal: formData.config?.enableModal ?? true,
    enableImpressions: Boolean(formData.config?.enableImpressions ?? false),
    targetings: normalizeGeoTargetings(formData.config?.targetings),
    enableInImage: formData.config?.enableInImage ?? false,
    enableFallback: formData.config?.enableFallback ?? true,
    enableUserPercentage: Boolean(formData.config?.enableUserPercentage ?? false),
    userPercentage: Number(formData.config?.userPercentage ?? 100),
    enablePageCounter: Boolean(formData.config?.enablePageCounter ?? false),
    pageCounterThreshold: Number(formData.config?.pageCounterThreshold ?? 3),
    adType: formData.config?.adType || 'gpt',
    timerDuration: formData.config?.timerDuration ?? 5,
    showDelay: formData.config?.showDelay ?? 2000,
    blurOffset: Number(formData.config?.blurOffset ?? 0),
    frequencyCappingHours: formData.config?.frequencyCappingHours ?? 2,
    hideIfNoAd: formData.config?.hideIfNoAd !== undefined ? formData.config.hideIfNoAd : true,
    logoUrl: formData.config?.logo || '',
  }

  // Handle GPT structure
  // Form has: gpt.adUnitPath (string path) and gpt.adSize (array of size pairs)
  // API should have: gpt.adUnitPath (string path) and gpt.adSize (array of sizes or pairs)
  const gptAdUnitPath = formData.config?.gpt?.adUnitPath || ''
  const gptAdUnitPathRewarded = formData.config?.gpt?.adUnitPathRewarded || ''
  const gptAdSize = formData.config?.gpt?.adSize || []

  // Convert adSize array to array of strings for API
  // Form: [[300, 250], [320, 100], 'fluid'] -> API: ["300x250", "320x100", "fluid"]
  const apiAdSize = []

  gptAdSize.forEach(size => {
    if (size === 'fluid') {
      apiAdSize.push('fluid')
    } else if (Array.isArray(size) && size.length === 2) {
      // Convert [300, 250] to "300x250" string
      apiAdSize.push(`${size[0]}x${size[1]}`)
    }
  })

  apiData.gpt = {
    // adUnitPath in API is string path
    adUnitPath: gptAdUnitPath || '/22247219933,227399588/Chordzaa_Adx/phoenixinterstitial',
    adUnitPathRewarded: gptAdUnitPathRewarded,
    siteDomain: formData.config?.gpt?.siteDomain || '',
    contentSelector: formData.config?.gpt?.contentSelector || '',
    // adSize in API is array of strings like ["300x250", "320x100", "fluid"]
    adSize: apiAdSize.length > 0 ? apiAdSize : ['300x250'],
  }

  // Handle AdSense structure
  apiData.adsense = {
    adClient: formData.config?.adsense?.adClient || null,
    adSlot: formData.config?.adsense?.adSlot || null,
    adSize: formData.config?.adsense?.adSize || [300, 250],
  }

  // Handle In-Image settings
  if (formData.config?.inImage) {
    const inImageApiAdSize = []
    const inImageAdSize = formData.config.inImage.adSize || []
    inImageAdSize.forEach(size => {
      if (size === 'fluid') {
        inImageApiAdSize.push('fluid')
      } else if (Array.isArray(size) && size.length === 2) {
        inImageApiAdSize.push(`${size[0]}x${size[1]}`)
      }
    })

    apiData.inImage = {
      contentSelector: formData.config.inImage.contentSelector || '',
      maxOverlap: formData.config.inImage.maxOverlap ?? 30,
      frequencyCappingHours: formData.config.inImage.frequencyCappingHours ?? 2,
      adSize: inImageApiAdSize.length > 0 ? inImageApiAdSize : ['300x250'],
      slots: (formData.config.inImage.slots || [])
        .map(slot => String(slot || '').trim())
        .filter(Boolean),
    }
  }

  // Handle texts structure
  if (formData.config?.texts) {
    apiData.texts = formData.config.texts
  }
  apiData.texts_options = formData.config?.textsOptions || []

  // Handle styles structure
  if (formData.config?.styles) {
    apiData.styles = { ...formData.config.styles }
    const modal = normalizeModalSize(formData.config.styles.modal)
    apiData.styles.modal = {
      ...modal,
      width: modal.width.map(modalDimensionCss),
      height: modal.height.map(modalDimensionCss),
    }
  }

  // Add _id if updating
  if (formData._id) {
    apiData._id = formData._id
  }

  if (!canEditPhoenixRate) delete apiData.phoenix_rate
  if (canEditProviderSettings) {
    apiData.provider_id = formData.provider_id ?? ''
    apiData.partner_rate = formData.partner_rate !== undefined && formData.partner_rate !== '' ? Number(formData.partner_rate) : 0
  }

  return { googlewidget: apiData }
}

// Validate disabled controls too: their values are retained and sent to the API.
export function displayConditionErrors(config = {}) {
  const errors = {}
  const fields = ['userPercentage', 'pageCounterThreshold']
  fields.forEach(field => {
    const value = config[field] ?? (field === 'userPercentage' ? 100 : 3)
    const number = Number(value)
    if (String(value).trim() === '' || !Number.isSafeInteger(number)
      || number < (field === 'userPercentage' ? 0 : 1)
      || (field === 'userPercentage' && number > 100)) {
      errors[field] = true
    }
  })
  return errors
}
